import React, { useMemo, useState } from "react";
import { PLANS, PLAN_CYCLES, priceOf, monthsOf, clampSeats } from "../../config/plans";
import { subscriptionService, storageService, customRequestService, branchRepo, staffRepo, customerRepo, ORDER_TYPE } from "../../services";
import { FEATURE_MODULES } from "../../config/feature-modules";
import { GB, formatBytes } from "../../config/storage";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, DataTable, Input, PageHead, ProgressBar, StatusBadge, baht, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";
import PaymentQrModal from "../../components/PaymentQrModal";
import ShopLinkCard from "../../components/ShopLinkCard";
import { useAddToCart } from "../../context/CartContext";
import { planItem } from "../../config/cart";

const QUOTA_ROWS = [
  { key: "branches", th: "สาขา", en: "Branches" },
  { key: "staff", th: "พนักงาน", en: "Staff" },
  { key: "customers", th: "ลูกค้า", en: "Customers" },
];

export default function Subscription() {
  const { organization, orgId, subscription, plan, profile, refreshOrganizations, toast, limitOf } = useApp();
  const { tx, pick } = useI18n();
  const [cycle, setCycle] = useState("monthly");
  const [buying, setBuying] = useState(null);
  // จำนวน User ที่เลือกต่อแพ็กเกจ (ค่าเริ่มต้น = จำนวนเดิมของร้านถ้าเป็นแพ็กเกจเดียวกัน)
  const [seatInput, setSeatInput] = useState({});
  const seatsFor = (item) =>
    clampSeats(item.key, seatInput[item.key] ?? (item.key === subscription.planKey && subscription.seats ? subscription.seats : item.minSeats));
  const { data: requests, reload: reloadRequests } = useAsync(() => customRequestService.listCustom(orgId), [orgId], []);
  const [quoting, setQuoting] = useState(false);
  const addToCart = useAddToCart();

  const { data: branches } = useAsync(() => branchRepo.all(orgId), [orgId], []);
  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);
  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const { data: usedBytes } = useAsync(() => storageService.usedBytes(orgId), [orgId], 0);
  const { data: history, reload: reloadHistory } = useAsync(() => subscriptionService.history(orgId), [orgId], []);

  const used = useMemo(
    () => ({ branches: branches?.length || 0, staff: staff?.length || 0, customers: customers?.length || 0 }),
    [branches, staff, customers]
  );

  const statusTone = subscription.status === "active" ? "green" : subscription.status === "expired" ? "rose" : "gray";
  const statusLabel =
    subscription.status === "active"
      ? tx("ใช้งานอยู่", "Active")
      : subscription.status === "expired"
      ? tx("หมดอายุแล้ว", "Expired")
      : tx("ใช้งานฟรี (จำกัด)", "Free (limited)");

  const buyingSeats = buying?.perUser ? seatsFor(buying) : 0;
  const amount = buying ? priceOf(buying.key, cycle, buyingSeats) : 0;

  // Ultimate: ส่งคำขอใบเสนอราคา ทีมงานติดต่อกลับ/เสนอราคาในแท็บฟีเจอร์ custom แล้วเปิดแพ็กเกจให้หลังเซ็นสัญญา
  const quoteTitle = (item) => `ขอใบเสนอราคาแพ็กเกจ ${item.name}`;
  const openQuote = (item) => (requests || []).find((row) => row.title === quoteTitle(item) && !["cancelled", "done"].includes(row.status));
  const requestQuote = async (item) => {
    setQuoting(true);
    try {
      await customRequestService.create({
        orgId,
        title: quoteTitle(item),
        description: `สนใจแพ็กเกจ ${item.name} (${item.detail}) ให้ทีมงานติดต่อกลับเพื่อประเมินขอบเขตงานและราคา`,
        by: profile?.fullname || profile?.email || "",
      });
      reloadRequests();
      toast(tx("ส่งคำขอแล้ว ทีมงานจะติดต่อกลับ", "Request sent — our team will contact you"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setQuoting(false);
    }
  };

  // จ่ายเงินสำเร็จ -> บันทึกใบเสร็จ + ต่ออายุร้าน (ถ้าขั้นนี้พังโมดัลจะโชว์ปุ่มลองใหม่ ไม่ตัดเงินซ้ำ)
  const onPaid = async (transaction) => {
    await subscriptionService.activate({
      organization,
      planKey: buying.key,
      cycle,
      seats: buyingSeats,
      amount,
      transaction,
    });
    await refreshOrganizations();
    reloadHistory();
    toast(tx(`เปิดใช้งาน ${buying.name} เรียบร้อย`, `${buying.nameEn} activated`), "success");
  };

  const buttonLabel = (item) => {
    if (!subscription.isActive) return tx("ซื้อแพ็กเกจนี้", "Buy this plan");
    if (item.key === subscription.planKey) return tx("ต่ออายุ", "Renew");
    const rank = PLANS.findIndex((row) => row.key === item.key);
    const current = PLANS.findIndex((row) => row.key === subscription.planKey);
    return rank > current ? tx("อัปเกรด", "Upgrade") : tx("เปลี่ยนเป็นแพ็กเกจนี้", "Switch to this plan");
  };

  return (
    <>
      <PageHead
        title={tx("แพ็กเกจการใช้งาน", "Subscription")}
        subtitle={tx("ยังไม่ซื้อแพ็กเกจก็ใช้งานฟรีได้ (ลูกค้า / สินค้า & บริการ / ใบเสนอราคา-ใบแจ้งหนี้ แบบจำกัดจำนวน) · ซื้อแพ็กเกจเพื่อปลดล็อกทุกเมนู", "Free use without a plan (customers / catalog / billing, limited) · buy a plan to unlock everything")}
      />

      {/* ---------- สถานะปัจจุบัน ---------- */}
      <Card
        title={tx("แพ็กเกจปัจจุบัน", "Current plan")}
        actions={<Badge tone={statusTone}>{statusLabel}</Badge>}
      >
        <div className="grid-3" style={{ gap: 14 }}>
          <div>
            <div className="u-fs-12 u-muted">{tx("แพ็กเกจ", "Plan")}</div>
            <div className="u-strong" style={{ fontSize: 20 }}>{plan ? pick(plan) : "-"}</div>
            <div className="u-fs-12 u-muted">
              {subscription.cycle ? pick(PLAN_CYCLES.find((row) => row.key === subscription.cycle) || {}) : ""}
              {plan?.perUser && subscription.seats ? ` · ${subscription.seats} User` : ""}
            </div>
          </div>
          <div>
            <div className="u-fs-12 u-muted">{tx("ใช้ได้ถึง", "Valid until")}</div>
            <div className="u-strong" style={{ fontSize: 20 }}>
              {subscription.expireAt ? formatDate(subscription.expireAt) : "-"}
            </div>
            <div className={`u-fs-12 ${subscription.daysLeft <= 7 ? "u-danger" : "u-muted"}`}>
              {subscription.isActive
                ? tx(`เหลืออีก ${subscription.daysLeft} วัน`, `${subscription.daysLeft} days left`)
                : tx("กรุณาชำระเงินเพื่อเปิดใช้งาน", "Please pay to activate")}
            </div>
          </div>
          <div>
            <div className="u-fs-12 u-muted">{tx("โมดูลที่ใช้ได้", "Modules included")}</div>
            <div className="u-strong" style={{ fontSize: 20 }}>
              {subscription.modules.length} / {FEATURE_MODULES.length}
            </div>
          </div>
        </div>

        {/* โควตาการใช้งาน */}
        <div className="u-mt-16" style={{ display: "grid", gap: 10 }}>
          {QUOTA_ROWS.map((row) => {
            const limit = limitOf(row.key);
            const unlimited = limit === null;
            const percent = unlimited ? 8 : Math.min(100, Math.round((used[row.key] / (limit || 1)) * 100));
            return (
              <div key={row.key}>
                <div className="u-between u-fs-12">
                  <span className="u-muted">{tx(row.th, row.en)}</span>
                  <span className={!unlimited && used[row.key] >= (limit || 0) ? "u-danger u-strong" : ""}>
                    {used[row.key]} / {unlimited ? tx("ไม่จำกัด", "Unlimited") : limit ?? 0}
                  </span>
                </div>
                <ProgressBar percent={percent} color={!unlimited && percent >= 100 ? "var(--c-rose)" : undefined} />
              </div>
            );
          })}

          {/* ที่เก็บไฟล์ (GB) — พื้นที่ที่ใช้คิดจากไฟล์ที่ระบบบันทึกไว้จริง */}
          {(() => {
            const limitGb = limitOf("storage");
            const unlimited = limitGb === null;
            const used = usedBytes || 0;
            const percent = unlimited ? 8 : Math.min(100, Math.round((used / ((limitGb || 0) * GB || 1)) * 100));
            const full = !unlimited && used >= (limitGb || 0) * GB;
            return (
              <div>
                <div className="u-between u-fs-12">
                  <span className="u-muted">{tx("ที่เก็บไฟล์", "Storage")}</span>
                  <span className={full ? "u-danger u-strong" : ""}>
                    {formatBytes(used)} / {unlimited ? tx("ไม่จำกัด", "Unlimited") : `${limitGb ?? 0} GB`}
                  </span>
                </div>
                <ProgressBar percent={percent} color={!unlimited && percent >= 100 ? "var(--c-rose)" : undefined} />
              </div>
            );
          })()}
        </div>
      </Card>

      {/* ---------- ลิงก์สมัครของลูกค้า (ร้านใหม่ที่ยังไม่ซื้อแพ็กเกจก็เห็น) ---------- */}
      <ShopLinkCard />

      {/* ---------- เลือกแพ็กเกจ ---------- */}
      <Card
        title={tx("เลือกแพ็กเกจ", "Choose a plan")}
        subtitle={tx("ราคายังไม่รวม VAT 7% · ชำระด้วย QR PromptPay เปิดใช้งานทันทีที่จ่ายสำเร็จ", "Excludes 7% VAT · pay by QR PromptPay, activated instantly")}
        actions={
          <div style={{ display: "flex", gap: 6 }}>
            {PLAN_CYCLES.map((item) => (
              <Button
                key={item.key}
                size="sm"
                variant={cycle === item.key ? "primary" : "ghost"}
                onClick={() => setCycle(item.key)}
              >
                {pick(item)}
              </Button>
            ))}
          </div>
        }
      >
        {cycle === "yearly" ? (
          <div className="u-fs-13 u-mb-16" style={{ color: "var(--c-indigo-600)" }}>
            <Icon name="gift" /> {tx("รอบรายปีจ่าย 10 เดือน ใช้งาน 12 เดือน", "Yearly: pay 10 months, use 12")}
          </div>
        ) : null}

        <div className="grid-3" style={{ gap: 12 }}>
          {PLANS.map((item) => {
            const isCurrent = subscription.isActive && item.key === subscription.planKey;
            const seats = item.perUser ? seatsFor(item) : 0;
            const quote = item.quoteOnly ? openQuote(item) : null;
            return (
              <div
                key={item.key}
                className="card-crm"
                style={{
                  borderColor: isCurrent ? "var(--c-indigo-600)" : undefined,
                  borderWidth: isCurrent || item.featured ? 2 : 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: 0,
                }}
              >
                {item.featured ? (
                  <span style={{ position: "absolute", top: -10, right: 12 }}>
                    <Badge tone="indigo">{tx("ยอดนิยม", "Popular")}</Badge>
                  </span>
                ) : null}

                <div className="u-strong" style={{ fontSize: 18 }}>{pick(item)}</div>
                <div className="u-fs-12 u-muted">{tx(item.detail, item.detailEn)}</div>
                {item.description ? <div className="u-fs-12 u-mt-8">{tx(item.description, item.descriptionEn)}</div> : null}

                {item.quoteOnly ? (
                  <div style={{ margin: "12px 0" }}>
                    <span style={{ fontSize: 24, fontWeight: 900 }}>{tx("ราคาตามใบเสนอราคา", "Custom quote")}</span>
                    <div className="u-fs-12 u-muted">{tx("ตามขอบเขตงาน + ค่าบำรุงรักษารายปี", "By scope + annual maintenance")}</div>
                  </div>
                ) : (
                  <div style={{ margin: "12px 0" }}>
                    <span style={{ fontSize: 30, fontWeight: 900 }}>{baht(priceOf(item.key, cycle, seats), 0)}</span>
                    <span className="u-fs-13 u-muted"> / {cycle === "yearly" ? tx("ปี", "year") : tx("เดือน", "month")}</span>
                    {item.perUser ? (
                      <div className="u-fs-12 u-muted">
                        {item.platformFee
                          ? tx(`ค่า Platform ${baht(item.platformFee, 0)} + ${baht(item.seatPrice, 0)} / User / เดือน`, `Platform ${baht(item.platformFee, 0)} + ${baht(item.seatPrice, 0)} / user / month`)
                          : tx(`${baht(item.seatPrice, 0)} / User / เดือน`, `${baht(item.seatPrice, 0)} / user / month`)}
                      </div>
                    ) : null}
                  </div>
                )}

                {item.perUser ? (
                  <Input
                    label={tx(`จำนวน User (${item.minSeats} - ${item.maxSeats})`, `Users (${item.minSeats} - ${item.maxSeats})`)}
                    type="number"
                    min={item.minSeats}
                    max={item.maxSeats}
                    value={seatInput[item.key] ?? seats}
                    onChange={(event) => setSeatInput({ ...seatInput, [item.key]: event.target.value })}
                    onBlur={() => setSeatInput({ ...seatInput, [item.key]: seats })}
                  />
                ) : null}

                <div style={{ display: "grid", gap: 6, alignContent: "start", flex: "1 1 auto" }}>
                  {item.highlights.map((line) => (
                    <div key={line.en} className="u-fs-13">
                      <span style={{ color: "var(--c-emerald, #10b981)" }}><Icon name="check" /></span> {tx(line.th, line.en)}
                    </div>
                  ))}
                  <div className="u-fs-12 u-muted u-mt-8">
                    {tx(`เปิดใช้งานได้ ${item.modules.length} โมดูล`, `${item.modules.length} modules included`)}
                    {" · "}
                    {item.limits.storage === null ? tx("ที่เก็บไฟล์ไม่จำกัด", "Unlimited storage") : tx(`ที่เก็บไฟล์ ${item.limits.storage} GB`, `${item.limits.storage} GB storage`)}
                  </div>
                </div>

                {item.quoteOnly ? (
                  isCurrent ? (
                    <div className="u-fs-12 u-muted u-mt-16">{tx("ต่ออายุผ่านทีมงานตามสัญญา", "Renewed by our team under your contract")}</div>
                  ) : (
                    <Button className="u-mt-16" block variant="primary" disabled={quoting || !!quote} onClick={() => requestQuote(item)}>
                      {quote ? tx("ส่งคำขอแล้ว รอทีมงานติดต่อกลับ", "Requested — we'll contact you") : tx("ขอใบเสนอราคา", "Request a quote")}
                    </Button>
                  )
                ) : (
                  <>
                    <Button
                      className="u-mt-16"
                      block
                      variant={isCurrent ? "ghost" : "primary"}
                      onClick={() => {
                        setSeatInput({ ...seatInput, [item.key]: seats });
                        setBuying(item);
                      }}
                    >
                      {buttonLabel(item)}
                    </Button>
                    <Button className="u-mt-8" block onClick={() => addToCart(planItem(item.key, cycle, seats))}>
                      <Icon name="cartPlus" /> {tx("ใส่ตะกร้า", "Add to cart")}
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* ---------- ประวัติการชำระ ---------- */}
      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx("ประวัติการชำระค่าแพ็กเกจ", "Billing history")}
      </div>
      <DataTable
        rows={history || []}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "planName", label: tx("แพ็กเกจ", "Plan"), render: (row) => `${row.planName}${row.seats ? ` · ${row.seats} User` : ""}` },
          {
            key: "cycle",
            label: tx("รอบ", "Cycle"),
            render: (row) => tx(`${row.months || monthsOf(row.cycle)} เดือน`, `${row.months || monthsOf(row.cycle)} months`),
          },
          { key: "expireAt", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => formatDate(row.expireAt) },
          { key: "refNo", label: "Ref" },
          { key: "amount", label: tx("ยอดชำระ", "Amount"), align: "right", render: (row) => baht(row.amount) },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) =>
              row.status === "manual" ? <Badge tone="amber">{tx("เปิดโดยทีมงาน", "Granted by our team")}</Badge> : <StatusBadge status={row.status} label={tx("ชำระแล้ว", "Paid")} />,
          },
        ]}
        emptyText={tx("ยังไม่มีประวัติการชำระเงิน", "No payments yet")}
      />

      <PaymentQrModal
        open={!!buying}
        title={buying ? `${buying.name} · ${cycle === "yearly" ? "รายปี" : "รายเดือน"}` : ""}
        subtitle={organization?.name}
        amount={amount}
        orderType={ORDER_TYPE.subscription}
        reference={`plan:${buying?.key || ""}:${cycle}${buyingSeats ? `:${buyingSeats}` : ""}`}
        items={
          buying
            ? [
                {
                  productName: `${buying.name}${buyingSeats ? ` ${buyingSeats} User` : ""} (${monthsOf(cycle)} เดือน)`,
                  productCode: `plan_${buying.key}_${cycle}`,
                  quantity: 1,
                  price: amount,
                  detail: { orgId, plan: buying.key, cycle, seats: buyingSeats || undefined },
                },
              ]
            : []
        }
        onPaid={onPaid}
        onClose={() => setBuying(null)}
      />
    </>
  );
}
